'use client';
import type { FC } from 'react';
import { useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { scroller } from 'react-scroll';
import { Media } from '~/utils/styles/enums';
import {
  OrderTabs,
  TabItem,
} from '@app/(default-layout)/se/[seriesId]/_components/order-tabs';
import {
  TAB_SERIES_ORDER_BY_DESC_HASH,
  TAB_SERIES_ORDER_BY_ASC_HASH,
} from '@app/(default-layout)/se/[seriesId]/_constants';
import { useHash } from '@app/_hooks/use-hash/use-hash';
import { HStack, Paragraph, VStack } from '@styled-system/jsx';

type Props = PropsWithChildren<{
  totalEpisodeCount: number;
}>;

export const OrderTabsContainer: FC<Props> = ({
  totalEpisodeCount,
  children,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabItem>(
    TabItem.ORDER_BY_DESC
  );
  const itemIndex = [TabItem.ORDER_BY_DESC, TabItem.ORDER_BY_ASC].indexOf(
    selectedTab
  );
  const [hash, setHash] = useHash();

  useEffect(() => {
    const target = hash?.replace('#', '');
    switch (target) {
      case TAB_SERIES_ORDER_BY_DESC_HASH: {
        setSelectedTab(TabItem.ORDER_BY_DESC);
        break;
      }
      case TAB_SERIES_ORDER_BY_ASC_HASH: {
        setSelectedTab(TabItem.ORDER_BY_ASC);
        break;
      }
    }

    // For PCs, specify an offset of the header height (72px) + margin 10px
    const scrollOffset =
      typeof window !== 'undefined' && window.matchMedia(Media.LARGE).matches
        ? -82
        : -10;
    scroller.scrollTo(target, {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      hashSpy: true,
      offset: scrollOffset,
    });
  }, [hash]);

  const onSelectTab = useCallback(
    (selectedItem: TabItem) => {
      setSelectedTab(selectedItem);
      switch (selectedItem) {
        case TabItem.ORDER_BY_DESC: {
          setHash(TAB_SERIES_ORDER_BY_DESC_HASH);
          break;
        }
        case TabItem.ORDER_BY_ASC: {
          setHash(TAB_SERIES_ORDER_BY_ASC_HASH);
          break;
        }
      }
    },
    [setHash]
  );

  return (
    <VStack w="100%" gap="0" id="tabs">
      <HStack
        justifyContent="space-between"
        px="4"
        py="2.5"
        w="100%"
        borderBottom="1px solid token(colors.separator)"
      >
        <Paragraph fontSize="size_14" fontWeight="bold">
          全{totalEpisodeCount.toLocaleString()}話
        </Paragraph>

        <OrderTabs currentSelected={selectedTab} onSelectTab={onSelectTab} />
      </HStack>
      {Array.isArray(children) ? children[itemIndex] : children}
    </VStack>
  );
};
